import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const HOME_PATHS = new Set(['/', '/home']);
const DISMISS_SESSION_KEY = 'artmosphere_pwa_install_dismissed';

function isHomePath(pathname) {
  return HOME_PATHS.has(pathname);
}

function isStandaloneInstalled() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function InstallInstructions({ title, steps, note }) {
  return (
    <div className="pwa-install-instructions mb-3 space-y-1.5">
      <p className="pwa-install-instructions__title">{title}</p>
      {steps.map((step, i) => (
        <p key={i}>{step}</p>
      ))}
      {note ? <p className="text-xs text-[#9a7a55] mt-1">{note}</p> : null}
    </div>
  );
}

const PWAInstallPrompt = () => {
  const location = useLocation();
  const deferredPromptRef = useRef(null);
  const desktopTimerRef = useRef(null);

  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [hasDeferredPrompt, setHasDeferredPrompt] = useState(false);
  const [browserInfo, setBrowserInfo] = useState({ name: '', isMobile: false, isTablet: false });

  const detectBrowserAndDevice = useCallback(() => {
    const ua = navigator.userAgent;
    let browserName = 'Unknown';
    const isMobile = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    const isTablet = /iPad|Android/i.test(ua) && !/Mobile/i.test(ua);

    if (ua.includes('Edg/') || ua.includes('Edge/')) {
      browserName = 'Edge';
    } else if (ua.includes('Chrome/') && !ua.includes('Edg/')) {
      browserName = 'Chrome';
    } else if (ua.includes('Safari/') && !ua.includes('Chrome/')) {
      browserName = 'Safari';
    } else if (ua.includes('Firefox/')) {
      browserName = 'Firefox';
    } else if (ua.includes('Opera/') || ua.includes('OPR/')) {
      browserName = 'Opera';
    }

    return { name: browserName, isMobile, isTablet };
  }, []);

  const clearDesktopTimer = () => {
    if (desktopTimerRef.current) {
      clearTimeout(desktopTimerRef.current);
      desktopTimerRef.current = null;
    }
  };

  const tryShowPrompt = useCallback(() => {
    try {
      if (sessionStorage.getItem(DISMISS_SESSION_KEY) === '1') return;
    } catch {
      /* ignore */
    }
    setShowInstallPrompt(true);
  }, []);

  useEffect(() => {
    if (!isHomePath(location.pathname)) {
      setShowInstallPrompt(false);
      clearDesktopTimer();
      return undefined;
    }

    if (isStandaloneInstalled()) {
      setIsInstalled(true);
      setShowInstallPrompt(false);
      return undefined;
    }

    setIsInstalled(false);

    const browserDeviceInfo = detectBrowserAndDevice();
    setBrowserInfo(browserDeviceInfo);

    const isDesktopBrowser = !browserDeviceInfo.isMobile && !browserDeviceInfo.isTablet;
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    clearDesktopTimer();

    // iOS: show manual instructions immediately
    if (isIOS) {
      tryShowPrompt();
    }

    // Desktop / non-Chrome-install: show after short delay (Safari never fires beforeinstallprompt)
    if (isDesktopBrowser) {
      desktopTimerRef.current = setTimeout(() => {
        if (!deferredPromptRef.current) {
          tryShowPrompt();
        }
      }, 1500);
    } else if (!isIOS) {
      // Android mobile: show after delay if install event never arrives
      desktopTimerRef.current = setTimeout(() => {
        tryShowPrompt();
      }, 2500);
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setHasDeferredPrompt(true);
      clearDesktopTimer();
      tryShowPrompt();
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowInstallPrompt(false);
      deferredPromptRef.current = null;
      setHasDeferredPrompt(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      clearDesktopTimer();
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [location.pathname, detectBrowserAndDevice, tryShowPrompt]);

  const handleInstallClick = async () => {
    const prompt = deferredPromptRef.current;
    if (!prompt) return;

    await prompt.prompt();
    const { outcome } = await prompt.userChoice;

    if (outcome === 'accepted') {
      setIsInstalled(true);
    }

    deferredPromptRef.current = null;
    setHasDeferredPrompt(false);
    setShowInstallPrompt(false);
  };

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_SESSION_KEY, '1');
    } catch {
      /* ignore */
    }
    setShowInstallPrompt(false);
  };

  if (isInstalled || !showInstallPrompt || !isHomePath(location.pathname)) {
    return null;
  }

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const { name: browserName, isMobile, isTablet } = browserInfo;

  const getInstallInstructions = () => {
    if (isIOS) {
      return (
        <InstallInstructions
          title="Add to Home Screen"
          steps={[
            <>1. Tap the <strong>Share</strong> button <span className="text-base">⎋</span> at the bottom</>,
            <>2. Scroll and select <strong>Add to Home Screen</strong></>,
            <>3. Tap <strong>Add</strong> in the top right</>,
          ]}
        />
      );
    }

    if (isMobile && !isIOS) {
      if (browserName === 'Chrome') {
        return (
          <InstallInstructions
            title="Install Artmosphere"
            steps={[
              <>1. Tap the <strong>Menu</strong> (⋮) in the top right</>,
              <>2. Select <strong>Install app</strong> or <strong>Add to Home screen</strong></>,
              <>3. Tap <strong>Install</strong> to confirm</>,
            ]}
          />
        );
      }
      if (browserName === 'Safari') {
        return (
          <InstallInstructions
            title="Add to Home Screen"
            steps={[
              <>1. Tap the <strong>Share</strong> button</>,
              <>2. Select <strong>Add to Home Screen</strong></>,
              <>3. Tap <strong>Add</strong></>,
            ]}
          />
        );
      }
      return (
        <InstallInstructions
          title="Install Artmosphere"
          steps={[
            'Look for the install option in your browser menu',
            'Or use Chrome for the best experience',
          ]}
        />
      );
    }

    if (isTablet) {
      if (browserName === 'Safari') {
        return (
          <InstallInstructions
            title="Add to Home Screen"
            steps={[
              <>1. Tap the <strong>Share</strong> button in Safari</>,
              <>2. Select <strong>Add to Home Screen</strong></>,
              <>3. Tap <strong>Add</strong></>,
            ]}
          />
        );
      }
      return (
        <InstallInstructions
          title="Install Artmosphere"
          steps={[
            "Look for the install icon in your browser's address bar",
            'Or check the browser menu for "Install app"',
          ]}
        />
      );
    }

    if (browserName === 'Chrome') {
      return (
        <InstallInstructions
          title="Install Artmosphere on Chrome"
          steps={[
            <>1. Click the <strong>install icon</strong> <span className="text-base">⊕</span> in the address bar</>,
            <>2. Or go to <strong>Menu (⋮)</strong> → <strong>Install Artmosphere...</strong></>,
            <>3. Click <strong>Install</strong> in the popup</>,
          ]}
        />
      );
    }
    if (browserName === 'Edge') {
      return (
        <InstallInstructions
          title="Install Artmosphere on Edge"
          steps={[
            <>1. Click the <strong>install icon</strong> <span className="text-base">⊕</span> in the address bar</>,
            <>2. Or go to <strong>Menu (⋯)</strong> → <strong>Apps</strong> → <strong>Install this site as an app</strong></>,
            <>3. Click <strong>Install</strong> to confirm</>,
          ]}
        />
      );
    }
    if (browserName === 'Safari') {
      return (
        <InstallInstructions
          title="Install Artmosphere on Safari"
          steps={[
            <>1. Go to <strong>File</strong> → <strong>Add to Dock</strong></>,
            <>2. Or drag the URL from the address bar to your Dock</>,
          ]}
          note="Safari on desktop has limited PWA support."
        />
      );
    }
    if (browserName === 'Firefox') {
      return (
        <InstallInstructions
          title="Install Artmosphere on Firefox"
          steps={[
            <>1. Click the <strong>menu</strong> (☰) → <strong>More tools</strong></>,
            <>2. Select <strong>Install Site as App</strong></>,
            <>3. Click <strong>Allow</strong> to install</>,
          ]}
        />
      );
    }
    return (
      <InstallInstructions
        title="Install Artmosphere"
        steps={[
          'Look for the install option in your browser menu',
          'For best experience, use Chrome or Edge',
        ]}
      />
    );
  };

  const subtitle =
    isMobile || isTablet
      ? 'Add Artmosphere to your home screen for quick access to your creative studio.'
      : 'Install Artmosphere as an app for one-click access to your museum of art.';

  return (
    <div
      className="fixed bottom-4 left-4 right-4 z-[200150] animate-slide-up md:left-auto md:right-4 md:max-w-md"
      role="dialog"
      aria-labelledby="pwa-install-title"
      aria-describedby="pwa-install-desc"
    >
      <div className="pwa-install-prompt p-4 md:p-5">
        <div className="pwa-install-prompt__rule -mx-4 -mt-4 mb-4 md:-mx-5 md:-mt-5 md:mb-4" />
        <div className="flex items-start gap-3">
          <div className="art-nav-logo flex h-11 w-11 shrink-0 items-center justify-center text-lg" aria-hidden>
            A
          </div>
          <div className="min-w-0 flex-1">
            <p className="auth-premium-eyebrow mb-1">Your creative studio</p>
            <h3 id="pwa-install-title" className="auth-premium-title mb-1 text-lg md:text-xl">
              Install Artmosphere
            </h3>
            <p id="pwa-install-desc" className="auth-premium-subtitle mb-3 text-sm leading-relaxed">
              {subtitle}
            </p>
            {hasDeferredPrompt ? (
              <button
                type="button"
                onClick={handleInstallClick}
                className="art-btn-primary rounded-xl px-5 py-2.5 text-sm font-semibold shadow-md md:text-base"
              >
                Install Now
              </button>
            ) : (
              getInstallInstructions()
            )}
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            className="auth-premium-close -mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xl leading-none"
            aria-label="Dismiss install prompt"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
